import { FC } from 'react';
import { Stack, Text, Group } from '@mantine/core';
import { format } from 'date-fns';

import { Modal } from 'src/components/Modal';
import { Button } from 'src/components/Button';
import { Form } from 'src/form';
import { InputText } from 'src/components/Form/Text';
import { InputDateTimePicker } from 'src/components/Form/DateTimePicker';
import { Select } from 'src/components/Form/Select';

import { useEditSlotModal } from './use-edit-slot-modal';

export const EditSlotModal: FC = () => {
  const {
    devices,
    interactions,
    brandings,
    isPending,
    isLoading,
    slot,
    minDate,
    maxDate,
    handleSave,
    handleClose,
    handleDelete,
    validation,
  } = useEditSlotModal();

  if (isLoading || !slot) {
    return;
  }

  return (
    <>
      <Modal
        opened={true}
        onClose={handleClose}
        title="Edit Campaign Slot"
        size="lg"
        closeOnClickOutside={!isPending}
        closeOnEscape={!isPending}
        withCloseButton={!isPending}
      >
        <Form
          initialValues={{
            name: slot.name,
            dateFrom: new Date(slot.date_from),
            dateTo: new Date(slot.date_to),
            deviceId: slot.device.id.toString(),
            interactionId: slot.interaction.id.toString(),
            brandingId: slot.branding.id.toString(),
          }}
          validate={validation}
          onSubmit={handleSave}
          loading={isPending}
        >
          <Stack gap="md">
            <Text size="sm" c="dimmed">
              Slot: {format(new Date(slot.date_from), 'EEEE, MMMM d')} -{' '}
              {format(new Date(slot.date_to), 'EEEE, MMMM d')}
            </Text>

            <InputText
              name="name"
              label="Slot Name"
              placeholder="Enter slot name"
            />

            <Group grow>
              <InputDateTimePicker
                name="dateFrom"
                label="Start Date & Time"
                placeholder="Select start date and time"
                minDate={minDate ? new Date(minDate) : undefined}
                maxDate={maxDate ? new Date(maxDate) : undefined}
              />
              <InputDateTimePicker
                name="dateTo"
                label="End Date & Time"
                placeholder="Select end date and time"
                minDate={minDate ? new Date(minDate) : undefined}
                maxDate={maxDate ? new Date(maxDate) : undefined}
              />
            </Group>

            <Select
              name="deviceId"
              label="Device"
              placeholder="Select device"
              data={devices.map((device) => ({
                value: device.id.toString(),
                label: device.playwall_id,
              }))}
            />

            <Select
              name="interactionId"
              label="Interaction"
              placeholder="Select interaction"
              data={interactions.map((interaction) => ({
                value: interaction.id.toString(),
                label: `${interaction.name} (${interaction.interaction_dictionary?.name || 'No dictionary'})`,
              }))}
            />

            <Select
              name="brandingId"
              label="Branding"
              placeholder="Select branding"
              data={brandings.map((branding) => ({
                value: branding.id.toString(),
                label: branding.name,
              }))}
            />

            <Group justify="space-between" mt="xl">
              <Button
                type="button"
                variant="danger"
                onClick={handleDelete}
                disabled={isPending}
              >
                Delete Slot
              </Button>

              <Group>
                <Button
                  type="button"
                  variant="outline"
                  onClick={handleClose}
                  disabled={isPending}
                >
                  Cancel
                </Button>
                <Button loading={isPending}>Save Changes</Button>
              </Group>
            </Group>
          </Stack>
        </Form>
      </Modal>
    </>
  );
};
